<!--
  @component
  Implement the preview for an Object field.
  @see https://decapcms.org/docs/widgets/#Object
  @see https://sveltiacms.app/en/docs/fields/object
-->
<script>
  import { VisibilityObserver } from '@sveltia/ui';

  import Subsection from '$lib/components/contents/details/fields/object/subsection.svelte';
  import FieldPreview from '$lib/components/contents/details/preview/field-preview.svelte';
  import { getEntryDraftContext } from '$lib/services/contents/draft/state.svelte';
  import { getFieldValueMapSnapshot } from '$lib/services/contents/draft/value-map.svelte';
  import { getKeysByPrefix } from '$lib/services/contents/entry/key-paths';
  import {
    getTypedSubKeyPath,
    resolveVariableType,
  } from '$lib/services/contents/fields/object/helpers';
  import { getFieldLabel } from '$lib/services/contents/i18n/options';

  /**
   * @import { FieldPreviewProps } from '$lib/types/private';
   * @import {
   * ObjectField,
   * ObjectFieldWithSubFields,
   * ObjectFieldWithTypes,
   * } from '$lib/types/public';
   */

  /**
   * @typedef {object} Props
   * @property {ObjectField} fieldConfig Field configuration.
   * @property {object | undefined} currentValue Field value.
   */

  const entryDraft = getEntryDraftContext();

  /** @type {FieldPreviewProps & Props} */
  let {
    /* eslint-disable prefer-const */
    locale,
    keyPath,
    typedKeyPath,
    fieldConfig,
    /* eslint-enable prefer-const */
  } = $props();

  const { fields } = $derived(/** @type {ObjectFieldWithSubFields} */ (fieldConfig));
  const { types, typeKey = 'type' } = $derived(/** @type {ObjectFieldWithTypes} */ (fieldConfig));
  const valueMap = $derived(getFieldValueMapSnapshot(entryDraft.current, locale, keyPath));
  // Ask the shared key path index rather than scanning the whole value map; see the same check in
  // the Object field editor
  const hasValues = $derived(
    getKeysByPrefix(valueMap, `${keyPath}.`).some((_keyPath) => !!valueMap[_keyPath]),
  );
  const hasVariableTypes = $derived(Array.isArray(types));
  const typeKeyPath = $derived(`${keyPath}.${typeKey}`);
  const type = $derived(hasVariableTypes ? valueMap[typeKeyPath] : undefined);
  const { typeConfig, unknownType, subFields } = $derived(
    resolveVariableType({ types, fields, type }),
  );
  const label = $derived(typeConfig ? getFieldLabel({ config: typeConfig, locale }) : undefined);
</script>

{#if hasValues}
  {#if unknownType}
    <!-- Unknown type: a warning is displayed in the editor -->
  {:else}
    <Subsection {label}>
      {#each subFields as subField (subField.name)}
        {@const subFieldKeyPath = `${keyPath}.${subField.name}`}
        <!-- Mark the subfield outside its observer, so that focusing it can scroll the preview to
          it even before it’s rendered -->
        <div role="none" data-lazy-key-path={subFieldKeyPath}>
          <VisibilityObserver>
            <FieldPreview
              keyPath={subFieldKeyPath}
              typedKeyPath={getTypedSubKeyPath({
                typedKeyPath,
                type: typeConfig?.name,
                subFieldName: subField.name,
              })}
              {locale}
              fieldConfig={subField}
            />
          </VisibilityObserver>
        </div>
      {/each}
    </Subsection>
  {/if}
{/if}
